import { useState, useEffect } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus, Loader2, CalendarDays, ListChecks, Settings2,
  Trash2, ToggleLeft, ToggleRight, Building2, Pencil,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { value: 0, label: 'Mon' },
  { value: 1, label: 'Tue' },
  { value: 2, label: 'Wed' },
  { value: 3, label: 'Thu' },
  { value: 4, label: 'Fri' },
  { value: 5, label: 'Sat' },
  { value: 6, label: 'Sun' },
];

export default function ClientSettingsPage() {
  const { user } = useAuth();
  const clientId = user?.managed_client_ids?.[0];

  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '', code: '', description: '',
    default_working_days: [0, 1, 2, 3, 4],
    allow_weekend_work: false, allow_holiday_work: false,
  });

  // Holidays
  const [holidays, setHolidays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [holidayDateStr, setHolidayDateStr] = useState('');
  const [holidayName, setHolidayName] = useState('');
  const [holidayType, setHolidayType] = useState('HOLIDAY');

  // Tasks
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [newTaskName, setNewTaskName] = useState('');
  const [newTaskCode, setNewTaskCode] = useState('');
  const [newTaskCategory, setNewTaskCategory] = useState('');
  const [savingTask, setSavingTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', code: '', category: '' });

  useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    fetchAll();
  }, [clientId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [clientRes, holidaysRes, tasksRes] = await Promise.all([
        axios.get(`${API_URL}/api/clients/${clientId}`),
        axios.get(`${API_URL}/api/clients/${clientId}/holidays`),
        axios.get(`${API_URL}/api/clients/${clientId}/tasks`),
      ]);
      const c = clientRes.data;
      setClient(c);
      setFormData({
        name: c.name, code: c.code, description: c.description || '',
        default_working_days: c.default_working_days || [0, 1, 2, 3, 4],
        allow_weekend_work: c.allow_weekend_work || false,
        allow_holiday_work: c.allow_holiday_work || false,
      });
      setHolidays(holidaysRes.data);
      setTasks(tasksRes.data);
    } catch {
      toast.error('Failed to load client settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(`${API_URL}/api/clients/${clientId}`, formData);
      toast.success('Settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleWorkingDay = (day) => {
    setFormData(prev => ({
      ...prev,
      default_working_days: prev.default_working_days.includes(day)
        ? prev.default_working_days.filter(d => d !== day)
        : [...prev.default_working_days, day].sort(),
    }));
  };

  // Holidays
  const handleAddHoliday = async () => {
    if (!holidayDateStr || !holidayName.trim()) {
      toast.error('Please select a date and enter a name');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/clients/${clientId}/holidays`, {
        client_id: clientId, date: holidayDateStr, name: holidayName, holiday_type: holidayType,
      });
      setHolidays(prev => [...prev, res.data].sort((a, b) => a.date.localeCompare(b.date)));
      toast.success('Holiday added');
      setHolidayDialogOpen(false);
      setHolidayDateStr('');
      setHolidayName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add holiday');
    }
  };

  const handleDeleteHoliday = async (holiday) => {
    try {
      await axios.delete(`${API_URL}/api/clients/${clientId}/holidays/${holiday.id}`);
      setHolidays(prev => prev.filter(h => h.id !== holiday.id));
      toast.success('Holiday removed');
    } catch {
      toast.error('Failed to remove holiday');
    }
  };

  const holidayDates = holidays.map(h => {
    const [y, m, d] = h.date.split('-').map(Number);
    return new Date(y, m - 1, d);
  });

  // Tasks
  const fetchTasks = async () => {
    setTasksLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/clients/${clientId}/tasks`);
      setTasks(res.data);
    } catch {
      toast.error('Failed to load tasks');
    } finally {
      setTasksLoading(false);
    }
  };

  const handleAddTask = async () => {
    if (!newTaskName.trim()) { toast.error('Task name is required'); return; }
    setSavingTask(true);
    try {
      await axios.post(`${API_URL}/api/clients/${clientId}/tasks`, {
        name: newTaskName.trim(),
        code: newTaskCode.trim(),
        category: (newTaskCategory && newTaskCategory !== '__none__') ? newTaskCategory : null,
      });
      setNewTaskName(''); setNewTaskCode(''); setNewTaskCategory('');
      await fetchTasks();
      toast.success('Task added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add task');
    } finally {
      setSavingTask(false);
    }
  };

  const handleToggleTask = async (task) => {
    try {
      await axios.put(`${API_URL}/api/clients/${clientId}/tasks/${task.id}`, {
        is_active: !task.is_active,
      });
      await fetchTasks();
    } catch {
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (task) => {
    if (task.is_system) { toast.error("The 'Other' task cannot be deleted"); return; }
    try {
      await axios.delete(`${API_URL}/api/clients/${clientId}/tasks/${task.id}`);
      await fetchTasks();
      toast.success('Task deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete task');
    }
  };

  const startEditTask = (task) => {
    setEditingTaskId(task.id);
    setEditForm({ name: task.name, code: task.code || '', category: task.category || '' });
  };

  const handleSaveTaskEdit = async (task) => {
    if (!editForm.name.trim()) { toast.error('Task name is required'); return; }
    try {
      await axios.put(`${API_URL}/api/clients/${clientId}/tasks/${task.id}`, {
        name: editForm.name.trim(),
        code: editForm.code.trim(),
        category: (editForm.category && editForm.category !== '__none__') ? editForm.category : null,
      });
      setEditingTaskId(null);
      await fetchTasks();
      toast.success('Task updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update task');
    }
  };

  if (!clientId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
        <Settings2 className="w-12 h-12 opacity-30" />
        <p className="text-lg font-medium">No client assigned</p>
        <p className="text-sm">Ask your administrator to assign you to a client.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{client?.name} — Settings</h1>
          <p className="text-muted-foreground text-sm">Manage working days, holidays, and task definitions</p>
        </div>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general"><Settings2 className="w-4 h-4 mr-1.5" />General</TabsTrigger>
          <TabsTrigger value="holidays"><CalendarDays className="w-4 h-4 mr-1.5" />Holidays ({holidays.length})</TabsTrigger>
          <TabsTrigger value="tasks"><ListChecks className="w-4 h-4 mr-1.5" />Tasks ({tasks.filter(t => !t.is_system).length})</TabsTrigger>
        </TabsList>

        {/* ── General ── */}
        <TabsContent value="general">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Client Information</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveSettings} className="space-y-5 max-w-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Client Name</Label>
                    <Input id="name" value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="code">Code</Label>
                    <Input id="code" value={formData.code}
                      onChange={e => setFormData({ ...formData, code: e.target.value.toUpperCase() })} required />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea id="description" value={formData.description} rows={2}
                    onChange={e => setFormData({ ...formData, description: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>Default Working Days</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS_OF_WEEK.map(day => (
                      <label key={day.value}
                        className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                          formData.default_working_days.includes(day.value)
                            ? 'bg-primary/10 border-primary' : 'bg-background border-border hover:bg-muted'
                        }`}>
                        <Checkbox
                          checked={formData.default_working_days.includes(day.value)}
                          onCheckedChange={() => toggleWorkingDay(day.value)}
                        />
                        <span className="text-sm">{day.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="space-y-3 pt-2 border-t">
                  <Label className="text-sm font-medium">Time Entry Permissions</Label>
                  <div className="flex items-center space-x-3">
                    <Checkbox id="allow_weekend"
                      checked={formData.allow_weekend_work}
                      onCheckedChange={v => setFormData({ ...formData, allow_weekend_work: v })} />
                    <label htmlFor="allow_weekend" className="text-sm cursor-pointer">
                      Allow employees to log time on <span className="font-medium text-orange-600">weekends</span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Checkbox id="allow_holiday"
                      checked={formData.allow_holiday_work}
                      onCheckedChange={v => setFormData({ ...formData, allow_holiday_work: v })} />
                    <label htmlFor="allow_holiday" className="text-sm cursor-pointer">
                      Allow employees to log time on <span className="font-medium text-red-600">holidays</span>
                    </label>
                  </div>
                </div>
                <Button type="submit" disabled={submitting}>
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Settings
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Holidays ── */}
        <TabsContent value="holidays">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Holiday Calendar</CardTitle>
                  <Button size="sm" onClick={() => setHolidayDialogOpen(true)}>
                    <Plus className="w-4 h-4 mr-1" />Add Holiday
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  modifiers={{ holiday: holidayDates }}
                  modifiersStyles={{
                    holiday: { backgroundColor: 'hsl(var(--destructive))', color: 'white', borderRadius: '50%' },
                  }}
                  className="rounded-md border"
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{holidays.length} Holiday{holidays.length !== 1 ? 's' : ''} Configured</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {holidays.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-sm">No holidays configured yet</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {holidays.map(h => (
                        <TableRow key={h.id}>
                          <TableCell className="text-sm">{format(new Date(h.date), 'MMM d, yyyy')}</TableCell>
                          <TableCell className="text-sm font-medium">{h.name}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">
                              {h.holiday_type}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                              onClick={() => handleDeleteHoliday(h)}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ── Tasks ── */}
        <TabsContent value="tasks">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Task Definitions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Add task form */}
              <div className="flex gap-2 flex-wrap">
                <Input placeholder="Task name (e.g., Design)" value={newTaskName}
                  onChange={e => setNewTaskName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleAddTask()}
                  className="flex-1 min-w-[160px]" />
                <Input placeholder="Code" value={newTaskCode}
                  onChange={e => setNewTaskCode(e.target.value.toUpperCase())}
                  className="w-24" />
                <Select value={newTaskCategory} onValueChange={setNewTaskCategory}>
                  <SelectTrigger className="w-28">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    <SelectItem value="CAPS">CAPS</SelectItem>
                    <SelectItem value="OPS">OPS</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={handleAddTask} disabled={savingTask}>
                  {savingTask ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}
                  Add Task
                </Button>
              </div>

              {tasksLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
              ) : tasks.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ListChecks className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">No tasks yet. An "Other" catch-all will be auto-created with your first task.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Task</TableHead>
                      <TableHead>Code</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead className="w-[120px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tasks.map(task => (
                      <TableRow key={task.id} className={!task.is_active ? 'opacity-50' : ''}>
                        {editingTaskId === task.id ? (
                          /* ── Inline edit ── */
                          <>
                            <TableCell colSpan={5}>
                              <div className="flex items-center gap-2 flex-wrap">
                                <Input
                                  value={editForm.name}
                                  onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                                  className="h-7 text-sm flex-1 min-w-[120px]"
                                  placeholder="Task name"
                                  autoFocus
                                />
                                <Input
                                  value={editForm.code}
                                  onChange={e => setEditForm({ ...editForm, code: e.target.value.toUpperCase() })}
                                  className="h-7 text-sm w-20 font-mono"
                                  placeholder="Code"
                                />
                                <Select value={editForm.category || '__none__'} onValueChange={v => setEditForm({ ...editForm, category: v })}>
                                  <SelectTrigger className="w-24 h-7 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="__none__">None</SelectItem>
                                    <SelectItem value="CAPS">CAPS</SelectItem>
                                    <SelectItem value="OPS">OPS</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button size="sm" className="h-7 px-2 text-xs" onClick={() => handleSaveTaskEdit(task)}>Save</Button>
                                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => setEditingTaskId(null)}>Cancel</Button>
                              </div>
                            </TableCell>
                          </>
                        ) : (
                          /* ── Normal display ── */
                          <>
                            <TableCell className="font-medium text-sm">{task.name}</TableCell>
                            <TableCell className="text-xs font-mono text-muted-foreground">{task.code || '—'}</TableCell>
                            <TableCell>
                              {task.category ? (
                                <Badge variant="outline" className={`text-xs ${
                                  task.category === 'CAPS'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                    : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                }`}>{task.category}</Badge>
                              ) : (
                                <span className="text-xs text-muted-foreground border border-dashed rounded px-1.5 py-0.5">No type</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={task.is_active
                                ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs'
                                : 'text-xs'}>
                                {task.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {task.is_system && <span className="text-xs text-muted-foreground italic">system</span>}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7" title="Edit"
                                  onClick={() => startEditTask(task)}>
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7"
                                  title={task.is_active ? 'Deactivate' : 'Activate'}
                                  onClick={() => handleToggleTask(task)}>
                                  {task.is_active
                                    ? <ToggleRight className="w-4 h-4 text-primary" />
                                    : <ToggleLeft className="w-4 h-4 text-muted-foreground" />}
                                </Button>
                                {!task.is_system && (
                                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                                    onClick={() => handleDeleteTask(task)}>
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Holiday Dialog */}
      <Dialog open={holidayDialogOpen} onOpenChange={setHolidayDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Holiday</DialogTitle>
            <DialogDescription>Add a non-working day for {client?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={holidayDateStr}
                onChange={e => setHolidayDateStr(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Name</Label>
              <Input placeholder="e.g., Christmas Day" value={holidayName}
                onChange={e => setHolidayName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={holidayType} onValueChange={setHolidayType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="HOLIDAY">Holiday</SelectItem>
                  <SelectItem value="NON_WORKING_DAY">Non-Working Day</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHolidayDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddHoliday}>Add Holiday</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
